import React, {
  createContext,
  useReducer,
  useMemo,
  useCallback,
  useContext,
  useEffect,
} from 'react';
import { useUser } from '@clerk/clerk-react';
import { useUserApplication } from './UserApplicationContext';
import {
  usePaymentQuery,
  usePaymentHistoryQuery,
  useCreateCheckoutSessionMutation,
  useVerifyPaymentSessionMutation,
} from '../hooks/queries/payment';
import { toErrorMessage } from '../utils/error';

const PaymentContext = createContext(null);

const initialState = {
  status: 'idle',
  error: null,
  // clientSecret is always stored together with the application it was minted
  // for, so a secret can never be adopted by a different application's checkout.
  clientSecret: null,
  clientSecretApplicationId: null,
};

function reducer(state, action) {
  switch (action.type) {
    case 'SET_STATUS':
      return { ...state, status: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload };
    case 'SET_CLIENT_SECRET':
      return {
        ...state,
        clientSecret: action.payload.clientSecret,
        clientSecretApplicationId: action.payload.applicationId,
      };
    case 'CLEAR_CLIENT_SECRET':
      return { ...state, clientSecret: null, clientSecretApplicationId: null };
    case 'RESET':
      return initialState;
    default:
      return state;
  }
}

export const PaymentProvider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const { user } = useUser();
  const {
    currentApplicationId,
    refreshApplications,
  } = useUserApplication();

  const {
    data: paymentData,
    isLoading: paymentQueryLoading,
    isFetching: paymentQueryFetching,
    error: paymentQueryError,
    refetch: refetchPayment,
  } = usePaymentQuery(currentApplicationId);

  const {
    data: paymentHistoryData,
    isLoading: paymentHistoryLoading,
    isFetching: paymentHistoryFetching,
    refetch: refetchPaymentHistory,
  } = usePaymentHistoryQuery(user?.id);

  const createCheckoutSessionMutation = useCreateCheckoutSessionMutation();
  const verifyPaymentSessionMutation = useVerifyPaymentSessionMutation();

  const setStatus = useCallback(
    (status) => dispatch({ type: 'SET_STATUS', payload: status }),
    []
  );

  const setError = useCallback(
    (message) => dispatch({ type: 'SET_ERROR', payload: message }),
    []
  );

  const clearPaymentData = useCallback(() => {
    dispatch({ type: 'RESET' });
  }, []);

  // Drop any held client secret as soon as the active application changes —
  // a Stripe session minted for one application must never leak into another.
  useEffect(() => {
    if (
      state.clientSecret &&
      state.clientSecretApplicationId !== currentApplicationId
    ) {
      dispatch({ type: 'CLEAR_CLIENT_SECRET' });
    }
  }, [currentApplicationId, state.clientSecret, state.clientSecretApplicationId]);

  const createCheckoutSession = useCallback(
    async (sessionData) => {
      if (!user?.id || !currentApplicationId) {
        throw new Error('User and application must be selected before starting checkout');
      }

      setStatus('processing');
      setError(null);

      try {
        const response = await createCheckoutSessionMutation.mutateAsync({
          ...sessionData,
          applicationId: currentApplicationId,
          userId: user.id,
        });

        if (response?.clientSecret) {
          dispatch({
            type: 'SET_CLIENT_SECRET',
            payload: {
              clientSecret: response.clientSecret,
              applicationId: currentApplicationId,
            },
          });
        }

        setStatus('completed');

        // Don't await these - let them run in background after checkout is displayed
        // The Stripe webhook will handle the actual payment completion update
        // Refreshing synchronously causes re-renders that disrupt the Stripe checkout
        Promise.all([refreshApplications(), refetchPayment()]).catch(err => {
          console.warn('Background refresh after checkout session creation failed:', err);
        });

        return response;
      } catch (err) {
        const message = toErrorMessage(err, 'Failed to create checkout session');
        setError(message);
        setStatus('error');
        throw err;
      }
    },
    [
      createCheckoutSessionMutation,
      currentApplicationId,
      refetchPayment,
      refreshApplications,
      setError,
      setStatus,
      user?.id,
    ]
  );

  const verifyPaymentSession = useCallback(
    async (sessionId) => {
      if (!currentApplicationId || !user?.id) {
        throw new Error('Missing application or user information');
      }

      setStatus('processing');
      setError(null);

      try {
        const response = await verifyPaymentSessionMutation.mutateAsync({
          sessionId,
          applicationId: currentApplicationId,
          userId: user.id,
        });

        await refreshApplications();
        await refetchPayment();
        await refetchPaymentHistory();

        setStatus('completed');
        return response;
      } catch (err) {
        const message = toErrorMessage(err, 'Failed to verify payment session');
        setError(message);
        setStatus('error');
        throw err;
      }
    },
    [
      currentApplicationId,
      refetchPayment,
      refetchPaymentHistory,
      refreshApplications,
      setError,
      setStatus,
      user?.id,
      verifyPaymentSessionMutation,
    ]
  );

  const loadPaymentHistory = useCallback(async () => {
    if (!user?.id) return;
    await refetchPaymentHistory();
  }, [refetchPaymentHistory, user?.id]);

  const refreshPaymentData = useCallback(async () => {
    await refetchPayment();
  }, [refetchPayment]);

  const queryErrorMessage = toErrorMessage(paymentQueryError);
  useEffect(() => {
    if (queryErrorMessage) {
      if (state.error !== queryErrorMessage) {
        setError(queryErrorMessage);
        setStatus('error');
      }
    } else if (state.error) {
      setError(null);
      if (state.status === 'error') {
        setStatus('idle');
      }
    }
  }, [queryErrorMessage, setError, setStatus, state.error, state.status]);

  const combinedError = state.error ?? queryErrorMessage ?? null;
  const isBusy =
    paymentQueryLoading ||
    paymentQueryFetching ||
    paymentHistoryLoading ||
    paymentHistoryFetching ||
    createCheckoutSessionMutation.isPending ||
    verifyPaymentSessionMutation.isPending;

  const value = useMemo(
    () => ({
      paymentData: paymentData || null,
      paymentHistory: paymentHistoryData || [],
      isLoading: isBusy,
      status: state.status,
      error: combinedError,
      hasError: Boolean(combinedError),
      // Only expose the secret when it belongs to the active application
      clientSecret:
        state.clientSecretApplicationId === currentApplicationId
          ? state.clientSecret
          : null,
      clientSecretApplicationId: state.clientSecretApplicationId,
      createCheckoutSession,
      verifyPaymentSession,
      loadPaymentHistory,
      clearPaymentData,
      refreshPaymentData,
      hasPayment: !!paymentData,
      isPaid:
        paymentData?.status === 'paid' ||
        paymentData?.paymentStatus === 'paid',
      isPending: paymentData?.status === 'pending',
      paymentAmount: paymentData?.amount || 0,
      paymentCurrency: paymentData?.currency || 'USD',
    }),
    [
      clearPaymentData,
      createCheckoutSession,
      createCheckoutSessionMutation.isPending,
      loadPaymentHistory,
      combinedError,
      isBusy,
      paymentData,
      paymentHistoryData,
      refreshPaymentData,
      currentApplicationId,
      state.clientSecret,
      state.clientSecretApplicationId,
      state.error,
      state.status,
      verifyPaymentSession,
      verifyPaymentSessionMutation.isPending,
    ]
  );

  return (
    <PaymentContext.Provider value={value}>
      {children}
    </PaymentContext.Provider>
  );
};

export const usePaymentContext = () => {
  const context = useContext(PaymentContext);
  if (!context) {
    throw new Error('usePaymentContext must be used within a PaymentProvider');
  }
  return context;
};

export default PaymentProvider;
